/**
 * Body plans.
 *
 * The first version of this grew one radial tree and got exactly what a radial tree
 * gives you: an octopus, every time. Tails are the only thing a tree can express.
 *
 * So bodies are now assembled from a *core solid* plus appendages. Each core is laid
 * out as concentric shells with explicit angles — effectively a Schlegel diagram —
 * which matters because the membrane force controls radius and the links control
 * angle, so between them a polyhedral graph resolves into a crisp figure rather than
 * a blob. Platonic solids come almost free from three families:
 *
 *   tetrahedron = pyramid(3)      cube = prism(4)      octahedron = antiprism(3)
 *   icosahedron = antiprism(5) + an apex at each end
 *
 * The dodecahedron is the one that needs writing out by hand.
 *
 * Every plan returns nodes in shell order, innermost first, so node 0 is always near
 * the centre. Metamorphosis reassigns roles by index, and keeping the centre the
 * centre is what stops a stage change from turning the body inside out.
 */
const TAU = Math.PI * 2;

/** Shell radii are in body units; the caller scales them. */
const ring = (n, radius, offset = 0) =>
  Array.from({ length: n }, (_, i) => ({
    restR: radius,
    angle: offset + (i / n) * TAU,
  }));

const cycle = (edges, base, n, kind = 'ring') => {
  for (let i = 0; i < n; i += 1) {
    edges.push({ source: base + i, target: base + ((i + 1) % n), kind });
  }
};

/** Wheel graph: an n-gonal pyramid seen from above. pyramid(3) is a tetrahedron. */
export const pyramid = (n) => {
  const nodes = [{ restR: 0, angle: 0 }, ...ring(n, 1)];
  const edges = [];
  for (let i = 0; i < n; i += 1) edges.push({ source: 0, target: 1 + i, kind: 'spoke' });
  cycle(edges, 1, n);
  return { nodes, edges, name: n === 3 ? 'tetrahedron' : `${n}-pyramid` };
};

/** Two stacked n-gons. prism(4) is a cube. */
export const prism = (n) => {
  const nodes = [...ring(n, 0.6), ...ring(n, 1.15)];
  const edges = [];
  cycle(edges, 0, n);
  cycle(edges, n, n);
  for (let i = 0; i < n; i += 1) {
    edges.push({ source: i, target: n + i, kind: 'spoke' });
  }
  return { nodes, edges, name: n === 4 ? 'cube' : `${n}-prism` };
};

/** Two n-gons offset by half a step, laced. antiprism(3) is an octahedron. */
export const antiprism = (n) => {
  const nodes = [...ring(n, 0.62), ...ring(n, 1.15, Math.PI / n)];
  const edges = [];
  cycle(edges, 0, n);
  cycle(edges, n, n);
  for (let i = 0; i < n; i += 1) {
    edges.push({ source: i, target: n + i, kind: 'spoke' });
    edges.push({ source: n + i, target: (i + 1) % n, kind: 'spoke' });
  }
  return { nodes, edges, name: n === 3 ? 'octahedron' : `${n}-antiprism` };
};

/** A pentagonal antiprism capped at both ends: 12 vertices, 30 edges, 5-regular. */
export const icosahedron = () => {
  const n = 5;
  const nodes = [
    { restR: 0, angle: 0 },
    ...ring(n, 0.62),
    ...ring(n, 1.12, Math.PI / n),
    { restR: 1.5, angle: Math.PI / n },
  ];
  const edges = [];
  const IN = 1;
  const OUT = 1 + n;
  const APEX = 1 + 2 * n;
  for (let i = 0; i < n; i += 1) {
    edges.push({ source: 0, target: IN + i, kind: 'spoke' });
    edges.push({ source: APEX, target: OUT + i, kind: 'spoke' });
    edges.push({ source: IN + i, target: OUT + i, kind: 'spoke' });
    edges.push({ source: OUT + i, target: IN + ((i + 1) % n), kind: 'spoke' });
  }
  cycle(edges, IN, n);
  cycle(edges, OUT, n);
  return { nodes, edges, name: 'icosahedron' };
};

/** 20 vertices, 30 edges, 3-regular — four pentagonal shells. */
export const dodecahedron = () => {
  const n = 5;
  const half = TAU / 10;
  const nodes = [
    ...ring(n, 0.34),
    ...ring(n, 0.62),
    ...ring(n, 0.95, half),
    ...ring(n, 1.24, half),
  ];
  const edges = [];
  const A = 0;
  const B = 5;
  const C = 10;
  const D = 15;
  cycle(edges, A, n);
  cycle(edges, D, n);
  for (let i = 0; i < n; i += 1) {
    edges.push({ source: A + i, target: B + i, kind: 'spoke' });
    edges.push({ source: B + i, target: C + i, kind: 'spoke' });
    edges.push({ source: B + i, target: C + ((i + n - 1) % n), kind: 'spoke' });
    edges.push({ source: C + i, target: D + i, kind: 'spoke' });
  }
  return { nodes, edges, name: 'dodecahedron' };
};

/** A square lattice patch — reads as a crystal rather than a creature. */
export const grid = (w, h) => {
  const nodes = [];
  const edges = [];
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  const span = Math.max(1, Math.hypot(cx, cy));
  const idx = (c, r) => r * w + c;
  for (let r = 0; r < h; r += 1) {
    for (let c = 0; c < w; c += 1) {
      const dx = c - cx;
      const dy = r - cy;
      nodes.push({ restR: Math.hypot(dx, dy) / span, angle: Math.atan2(dy, dx) });
      if (c + 1 < w) edges.push({ source: idx(c, r), target: idx(c + 1, r), kind: 'ring' });
      if (r + 1 < h) edges.push({ source: idx(c, r), target: idx(c, r + 1), kind: 'ring' });
    }
  }
  return shellOrder({ nodes, edges, name: `${w}x${h} lattice` });
};


/** A star polygon: one ring wired to every `step`-th neighbour. Pure geometry. */
export const starPolygon = (n, step) => {
  const nodes = ring(n, 1);
  const edges = [];
  cycle(edges, 0, n);
  for (let i = 0; i < n; i += 1) {
    edges.push({ source: i, target: (i + step) % n, kind: 'chord' });
  }
  return { nodes, edges, name: `{${n}/${step}} star` };
};

/** n-gonal bipyramid: a ring capped above and below, flattened into the plane. */
export const bipyramid = (n) => {
  const nodes = [{ restR: 0, angle: 0 }, ...ring(n, 1), { restR: 1.62, angle: 0 }];
  const edges = [];
  const APEX = n + 1;
  for (let i = 0; i < n; i += 1) {
    edges.push({ source: 0, target: 1 + i, kind: 'spoke' });
    edges.push({ source: APEX, target: 1 + i, kind: 'spoke' });
  }
  cycle(edges, 1, n);
  return { nodes, edges, name: `${n}-bipyramid` };
};

/** A tower of antiprisms seen from above — concentric rings, each half-step offset. */
export const antiprismStack = (n, layers) => {
  const nodes = [];
  const edges = [];
  for (let L = 0; L < layers; L += 1) {
    const base = L * n;
    nodes.push(...ring(n, (L + 1) / layers, (L % 2) * (Math.PI / n)));
    cycle(edges, base, n);
    if (L === 0) continue;
    const prev = base - n;
    for (let i = 0; i < n; i += 1) {
      edges.push({ source: prev + i, target: base + i, kind: 'spoke' });
      edges.push({ source: base + i, target: prev + ((i + 1) % n), kind: 'spoke' });
    }
  }
  return { nodes, edges, name: `${n}-gon x${layers} tower` };
};

/**
 * Hexagonal lattice on axial coordinates — a honeycomb.
 *
 * Neighbours are taken in three of the six directions only, so each edge is emitted
 * once rather than from both ends.
 */
export const hexLattice = (rings) => {
  const cells = [];
  const index = new Map();
  for (let q = -rings; q <= rings; q += 1) {
    for (let r = Math.max(-rings, -q - rings); r <= Math.min(rings, -q + rings); r += 1) {
      index.set(`${q},${r}`, cells.length);
      cells.push([q, r]);
    }
  }
  const pos = cells.map(([q, r]) => [1.5 * q, Math.sqrt(3) * (r + q / 2)]);
  const span = Math.max(1e-6, Math.max(...pos.map(([x, y]) => Math.hypot(x, y))));
  const nodes = pos.map(([x, y]) => ({
    restR: Math.hypot(x, y) / span,
    angle: Math.atan2(y, x),
  }));
  const edges = [];
  cells.forEach(([q, r], i) => {
    [[1, 0], [0, 1], [-1, 1]].forEach(([dq, dr]) => {
      const j = index.get(`${q + dq},${r + dr}`);
      if (j !== undefined) edges.push({ source: i, target: j, kind: 'ring' });
    });
  });
  return shellOrder({ nodes, edges, name: `${rings}-ring honeycomb` });
};

/**
 * Reorder a plan innermost-first.
 *
 * Every plan has to satisfy this: node 0 must be near the centre, because
 * metamorphosis reassigns roles by index and a body that renumbers its centre turns
 * itself inside out.
 */
const shellOrder = (plan) => {
  const order = plan.nodes
    .map((_, i) => i)
    .sort((a, b) => plan.nodes[a].restR - plan.nodes[b].restR);
  const remap = new Map(order.map((k, i) => [k, i]));
  return {
    name: plan.name,
    nodes: order.map((k) => plan.nodes[k]),
    edges: plan.edges.map((e) => ({
      source: remap.get(e.source),
      target: remap.get(e.target),
      kind: e.kind,
    })),
  };
};

/** Concentric rings with spokes and chords — the ornamental end of the range. */
export const rosette = (shells, symmetry = 6) => {
  const nodes = [{ restR: 0, angle: 0 }];
  const edges = [];
  const starts = [0];
  for (let s = 1; s <= shells; s += 1) {
    starts.push(nodes.length);
    const count = symmetry * s;
    nodes.push(...ring(count, s / shells, s % 2 ? 0 : Math.PI / count));
  }
  for (let s = 1; s <= shells; s += 1) {
    const count = symmetry * s;
    const base = starts[s];
    const prevCount = s === 1 ? 1 : symmetry * (s - 1);
    const prevBase = starts[s - 1];
    for (let i = 0; i < count; i += 1) {
      edges.push({
        source: prevBase + (prevCount === 1 ? 0 : Math.floor((i / count) * prevCount)),
        target: base + i,
        kind: 'spoke',
      });
    }
    cycle(edges, base, count);
    if (s >= 2) {
      const step = Math.max(2, Math.round(count / symmetry));
      for (let i = 0; i < count; i += 1) {
        edges.push({ source: base + i, target: base + ((i + step) % count), kind: 'chord' });
      }
    }
  }
  return { nodes, edges, name: `${shells}-shell rosette` };
};

/**
 * Appendages, attached to whichever nodes sit furthest out.
 *
 * Their rest radii continue past the rim so they hang off the body rather than
 * folding back into it, and each carries a slight angular offset from its host so a
 * pair of stalks on neighbouring nodes does not overlap into one thick stem.
 *
 * The radial steps are deliberately short. At a third of a body radius per segment a
 * five-node tail reached 2.7x the core, so the solid — the entire point of the
 * creature — ended up a small island in the middle of its own tentacles. Limbs are
 * accents; they should not out-measure the thing they hang off.
 */
const rimNodes = (body, wanted, rnd) => {
  // Core hosts only. Ranking across every node let a tail hang off the tip of an
  // earlier tail, and those chains compounded outward until the limbs out-measured
  // the solid they were decorating.
  const ranked = body.nodes
    .map((n, i) => ({ i, restR: n.restR, limb: n.limb }))
    .filter((n) => !n.limb)
    .sort((a, b) => b.restR - a.restR);
  const pool = ranked.slice(0, Math.max(1, Math.ceil(ranked.length * 0.6)));
  const picked = [];
  for (let k = 0; k < wanted && pool.length; k += 1) {
    picked.push(pool.splice(Math.floor(rnd() * pool.length), 1)[0].i);
  }
  return picked;
};

export const addTail = (body, host, length, rnd) => {
  let prev = host;
  const base = body.nodes[host];
  for (let k = 0; k < length; k += 1) {
    const id = body.nodes.length;
    body.nodes.push({
      restR: base.restR + 0.13 * (k + 1),
      angle: base.angle + (rnd() - 0.5) * 0.22,
      limb: true,
    });
    body.edges.push({ source: prev, target: id, kind: 'limb' });
    prev = id;
  }
  return prev;
};

/** A stem with a little head on it. */
export const addStalk = (body, host, stemLength, petals, rnd) => {
  const tip = addTail(body, host, stemLength, rnd);
  const base = body.nodes[tip];
  const first = body.nodes.length;
  for (let k = 0; k < petals; k += 1) {
    body.nodes.push({
      restR: base.restR + 0.11,
      angle: base.angle + (k - (petals - 1) / 2) * 0.34,
      limb: true,
      petal: true,
    });
    body.edges.push({ source: tip, target: first + k, kind: 'limb' });
  }
  // Close the head into a fan, so it reads as a flower rather than as more tail.
  for (let k = 0; k + 1 < petals; k += 1) {
    body.edges.push({ source: first + k, target: first + k + 1, kind: 'limb' });
  }
};

export const addCilia = (body, hosts, rnd) => {
  hosts.forEach((host) => {
    const base = body.nodes[host];
    const id = body.nodes.length;
    body.nodes.push({
      restR: base.restR + 0.12,
      angle: base.angle + (rnd() - 0.5) * 0.3,
      limb: true,
    });
    body.edges.push({ source: host, target: id, kind: 'limb' });
  });
};

/**
 * Cores that can host a given node budget.
 *
 * A core is only a candidate if it uses at least half the budget — otherwise a
 * 20-node creature comes out as a tetrahedron wearing sixteen tentacles, which is
 * the octopus problem again by another route.
 */
const CORES = [
  { size: 4, make: () => pyramid(3) },
  { size: 5, make: () => pyramid(4) },
  { size: 6, make: () => antiprism(3) },
  { size: 6, make: () => prism(3) },
  { size: 6, make: () => bipyramid(4) },
  { size: 7, make: () => pyramid(6) },
  { size: 7, make: () => starPolygon(7, 2) },
  { size: 8, make: () => prism(4) },
  { size: 8, make: () => antiprism(4) },
  { size: 8, make: () => bipyramid(6) },
  { size: 9, make: () => grid(3, 3) },
  { size: 9, make: () => starPolygon(9, 4) },
  { size: 10, make: () => prism(5) },
  { size: 10, make: () => antiprism(5) },
  { size: 11, make: () => starPolygon(11, 3) },
  { size: 12, make: () => prism(6) },
  { size: 12, make: () => icosahedron() },
  { size: 12, make: () => grid(4, 3) },
  { size: 13, make: () => starPolygon(13, 5) },
  { size: 14, make: () => antiprism(7) },
  { size: 16, make: () => grid(4, 4) },
  { size: 16, make: () => prism(8) },
  { size: 19, make: () => hexLattice(2) },
  { size: 19, make: () => rosette(2, 6) },
  { size: 20, make: () => dodecahedron() },
  { size: 20, make: () => grid(5, 4) },
  { size: 25, make: () => grid(5, 5) },
  { size: 31, make: () => rosette(2, 10) },
  { size: 37, make: () => hexLattice(3) },
  { size: 37, make: () => rosette(3, 6) },
  { size: 43, make: () => rosette(3, 7) },
  { size: 61, make: () => hexLattice(4) },
  { size: 61, make: () => rosette(4, 6) },
  { size: 91, make: () => hexLattice(5) },
  { size: 91, make: () => rosette(5, 6) },
];

/**
 * Larger plans, generated rather than listed.
 *
 * Past a hundred nodes the interesting variation is in the family and its parameters,
 * not in named solids, and writing out every rosette and honeycomb by hand would be a
 * hundred near-identical lines. Sizes are computed from the closed forms so the
 * catalogue and the plans cannot drift apart.
 */
(() => {
  for (let sym = 7; sym <= 16; sym += 1) {
    for (let sh = 4; sh <= 14; sh += 1) {
      const size = 1 + (sym * sh * (sh + 1)) / 2;
      if (size > 110 && size <= 1050) CORES.push({ size, make: () => rosette(sh, sym) });
    }
  }
  for (let w = 11; w <= 33; w += 2) {
    CORES.push({ size: w * w, make: () => grid(w, w) });
    CORES.push({ size: w * (w - 4), make: () => grid(w, w - 4) });
  }
  for (let r = 6; r <= 18; r += 1) {
    CORES.push({ size: 1 + 3 * r * (r + 1), make: () => hexLattice(r) });
  }
  for (let n = 6; n <= 20; n += 2) {
    for (let L = 3; L <= 14; L += 1) {
      CORES.push({ size: n * L, make: () => antiprismStack(n, L) });
    }
  }
  for (let n = 17; n <= 401; n += 12) {
    CORES.push({ size: n, make: () => starPolygon(n, Math.max(2, Math.round(n / 2.6))) });
  }
})();

/**
 * Pick a core for a node budget.
 *
 * Narrowed progressively rather than drawn uniformly from everything that fits. A
 * uniform draw across [0.5, 1] of the budget systematically undershoots — asked for
 * 800 nodes it would happily return a 561-node core and leave a third of the budget
 * to limbs — and because each stage draws independently, a later stage could come out
 * *smaller* than an earlier one and the creature would visibly shrink mid-scroll.
 *
 * Taking the tightest non-empty band keeps the delivered size close to the request
 * while still leaving several families to choose between at any given size.
 */
export const coreFor = (budget, rnd) => {
  const fits = CORES.filter((c) => c.size <= budget);
  if (!fits.length) return null;
  const band =
    fits.filter((c) => c.size >= budget * 0.85).length
      ? fits.filter((c) => c.size >= budget * 0.85)
      : fits.filter((c) => c.size >= budget * 0.7).length
      ? fits.filter((c) => c.size >= budget * 0.7)
      : fits;
  return band[Math.floor(rnd() * band.length)];
};

export { rimNodes };
